import { useEffect, useMemo, useState } from "react";
import { api, fmtPct, fmtUsd } from "@/lib/api";
import { useLiveQuotes } from "@/context/LiveMarketContext";
import { LiveStamp } from "@/components/LiveStamp";
import { FlashPrice } from "@/components/FlashPrice";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, RotateCcw, Wallet, PiggyBank, TrendingUp } from "lucide-react";
import TradeModal from "@/components/TradeModal";
import { Hint } from "@/components/Hint";
import { Term } from "@/components/Term";
import { BeginnerNote } from "@/components/Beginner";

const Stat = ({ k, label, value, accent, hint, hintKey, icon: Icon }) => (
  <div className="apx-card p-4">
    <div className="flex items-center justify-between mb-1">
      <div className="text-[10px] font-mono-ticker uppercase tracking-widest text-slate-500"><Term k={k} side="bottom" iconClass="normal-case" testid={`pf-term-${k}`}>{label}</Term></div>
      {Icon && <Icon className="w-3.5 h-3.5 text-slate-500" />}
    </div>
    <div className={`font-mono-ticker text-xl md:text-2xl font-semibold ${accent || "text-slate-100"}`}>{value}</div>
    {hint && <div className="text-xs text-slate-500 mt-0.5">{hintKey ? <Term k={hintKey} side="bottom" testid={`pf-term-${hintKey}`}>{hint}</Term> : hint}</div>}
  </div>
);

// Table header → glossary key (positions and trade-history tables).
const Th = ({ k, tid, children, className = "" }) => <th className={`px-4 py-2 ${className}`}>{k ? <Term k={k} side="bottom" iconClass="normal-case" testid={tid || `pf-th-${k}`}>{children}</Term> : children}</th>;

export default function Portfolio() {
  const [pf, setPf] = useState(null);
  const { byId: liveById } = useLiveQuotes(useMemo(() => (pf?.positions || []).map((p) => ({ symbol: p.symbol, id: p.coin_id })), [pf]));
  const lq = (p) => { const q = liveById[p.coin_id]; return q && q.price != null ? q : null; };
  const [stats, setStats] = useState(null);
  const [trades, setTrades] = useState([]);
  const [modal, setModal] = useState({ open: false, side: "buy", coin: null });
  const nav = useNavigate();

  const load = async () => {
    try {
      const [p, s, t] = await Promise.all([
        api.get("/portfolio"),
        api.get("/portfolio/stats"),
        api.get("/portfolio/trades"),
      ]);
      setPf(p.data); setStats(s.data); setTrades(t.data);
    } catch (e) { console.warn("[apex] ignored:", e); }
  };

  useEffect(() => { load(); }, []);

  const reset = async () => {
    if (!confirm("Reset portfolio? All positions and trade history will be cleared and your virtual balance will return to $100,000.")) return;
    try {
      await api.post("/portfolio/reset");
      toast.success("Portfolio reset");
      load();
    } catch { toast.error("Reset failed"); }
  };

  if (!pf) return <div className="p-6 text-slate-500">Loading…</div>;

  const pnlUp = pf.total_pnl >= 0;

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold">Paper Portfolio</h1>
          <p className="text-sm text-slate-400 mt-1">Simulated trading · $100,000 virtual starting balance</p>
        </div>
        <Hint k="reset" side="left" asChild>
        <button
          data-testid="reset-portfolio-btn"
          onClick={reset}
          className="btn-ghost rounded-lg px-3 py-2 text-sm flex items-center gap-2"
        >
          <RotateCcw className="w-4 h-4" /> Reset
        </button>
        </Hint>
      </div>

      <BeginnerNote testid="portfolio-beginner-note">
        <strong className="text-slate-300">What you're looking at:</strong> this is a practice account with virtual money — real live prices, zero real risk. <strong className="text-slate-300">Total value</strong> = cash + what your coins are worth right now. <strong className="text-slate-300">Unrealized P&L</strong> is the gain/loss on coins you still hold (it changes every second); <strong className="text-slate-300">realized P&L</strong> is locked in from trades you have closed. Green = gain, red = loss.
      </BeginnerNote>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat
          k="total_value" label="Total Value" icon={Wallet}
          value={<span data-testid="portfolio-total">{fmtUsd(pf.total_value)}</span>}
          accent={pnlUp ? "text-emerald-400" : "text-red-400"}
          hint={`${fmtPct(pf.total_pnl_pct)} all-time`}
        />
        <Stat k="cash" label="Cash" icon={PiggyBank} value={fmtUsd(pf.cash)} hint="Available for new buys" />
        <Stat k="holdings_value" label="Holdings Value" value={fmtUsd(pf.holdings_value)} />
        <Stat
          k="realized_pnl" label="Realized P&L" icon={TrendingUp}
          value={fmtUsd(stats?.realized_pnl ?? 0)}
          accent={(stats?.realized_pnl ?? 0) >= 0 ? "text-emerald-400" : "text-red-400"}
          hint={stats ? `${stats.wins}W / ${stats.losses}L · ${stats.win_rate}% win rate` : ""}
          hintKey="pt_win_rate"
        />
      </div>

      <div className="apx-card">
        <div className="p-4 border-b border-slate-800 flex items-center justify-between">
          <h3 className="font-display text-base font-semibold">Positions</h3>
          <span className="text-[10px] font-mono-ticker uppercase tracking-widest text-slate-500">{pf.positions.length} open</span>
        </div>
        {pf.positions.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500" data-testid="no-positions">
            No positions yet. Open any coin and tap Buy to start paper trading.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] font-mono-ticker uppercase tracking-widest text-slate-500 border-b border-slate-900">
                  <th className="text-left px-4 py-2">Asset</th>
                  <Th k="pt_qty" className="text-right">Qty</Th>
                  <Th k="pt_avg_cost" className="text-right hidden sm:table-cell">Avg Cost</Th>
                  <Th k="pt_live_price" className="text-right">Price</Th>
                  <Th k="pt_change_24h" className="text-right hidden md:table-cell">24h</Th>
                  <Th k="pt_position_value" className="text-right">Value</Th>
                  <Th k="pt_position_pnl" className="text-right">P&L</Th>
                  <th className="text-right px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {pf.positions.map((p) => (
                  <tr key={p.coin_id} data-testid={`pos-${p.coin_id}`}
                      className="border-b border-slate-900 hover:bg-slate-800/30">
                    <td className="px-4 py-3">
                      <button onClick={() => nav(`/coin/${p.coin_id}`)} className="flex items-center gap-2 text-left">
                        {p.image && <img src={p.image} className="w-6 h-6 rounded-full" alt="" />}
                        <div>
                          <div className="font-medium">{p.name}</div>
                          <div className="text-[10px] font-mono-ticker uppercase text-slate-500">{p.symbol}</div>
                        </div>
                      </button>
                    </td>
                    <td className="text-right font-mono-ticker px-4">{p.qty.toFixed(6)}</td>
                    <td className="text-right font-mono-ticker px-4 hidden sm:table-cell">{fmtUsd(p.avg_cost)}</td>
                    <td className="text-right font-mono-ticker px-4" data-testid={`pos-price-${p.coin_id}`}><FlashPrice value={lq(p)?.price ?? p.price} tid={`pos-flash-${p.coin_id}`}>{fmtUsd(lq(p)?.price ?? p.price)}</FlashPrice>{lq(p) && <div><LiveStamp at={lq(p).at} state={lq(p).state} feed={lq(p).feed} reason={lq(p).reason} source={lq(p).sourceLabel} tid={`pos-stamp-${p.coin_id}`} /></div>}</td>
                    <td className={`text-right font-mono-ticker px-4 hidden md:table-cell ${(lq(p)?.change24 ?? p.change_24h) >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                      {fmtPct(lq(p)?.change24 ?? p.change_24h)}
                    </td>
                    <td className="text-right font-mono-ticker px-4"><FlashPrice value={lq(p) ? lq(p).price * p.qty : p.market_value}>{fmtUsd(lq(p) ? lq(p).price * p.qty : p.market_value)}</FlashPrice></td>
                    <td className={`text-right font-mono-ticker px-4 ${(lq(p) ? lq(p).price * p.qty - p.avg_cost * p.qty : p.pnl) >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                      <FlashPrice value={lq(p) ? lq(p).price * p.qty - p.avg_cost * p.qty : p.pnl}>{fmtUsd(lq(p) ? lq(p).price * p.qty - p.avg_cost * p.qty : p.pnl)}</FlashPrice><br />
                      <span className="text-[10px]">{fmtPct(lq(p) && p.avg_cost ? ((lq(p).price - p.avg_cost) / p.avg_cost) * 100 : p.pnl_pct)}</span>
                    </td>
                    <td className="text-right px-4 py-3">
                      <div className="flex gap-1 justify-end">
                        <Hint k="buy_more" asChild>
                        <button
                          data-testid={`buy-more-${p.coin_id}`}
                          onClick={() => setModal({ open: true, side: "buy", coin: { id: p.coin_id, symbol: p.symbol, name: p.name, price: p.price } })}
                          className="px-2 py-1 rounded bg-emerald-950/60 text-emerald-300 border border-emerald-900 text-xs"
                        >
                          <ArrowUp className="w-3 h-3" />
                        </button>
                        </Hint>
                        <Hint k="sell_pos" asChild>
                        <button
                          data-testid={`sell-${p.coin_id}`}
                          onClick={() => setModal({ open: true, side: "sell", coin: { id: p.coin_id, symbol: p.symbol, name: p.name, price: p.price } })}
                          className="px-2 py-1 rounded bg-red-950/60 text-red-300 border border-red-900 text-xs"
                        >
                          <ArrowDown className="w-3 h-3" />
                        </button>
                        </Hint>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="apx-card">
        <div className="p-4 border-b border-slate-800">
          <h3 className="font-display text-base font-semibold">Trade History</h3>
        </div>
        {trades.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">No trades yet.</div>
        ) : (<>
          <ul className="sm:hidden divide-y divide-slate-900" data-testid="trades-cards">
            {trades.map((t) => (
              <li key={t.id} className="px-4 py-3 flex items-center gap-3">
                <span className={`text-[10px] font-mono-ticker font-bold px-2 py-1 rounded ${t.side === "buy" ? "bg-emerald-500/15 text-emerald-300" : "bg-red-500/15 text-red-300"}`}>{t.side.toUpperCase()}</span>
                <div className="min-w-0 flex-1">
                  <div className="font-mono-ticker uppercase text-sm">{t.symbol} <span className="text-slate-500 text-xs normal-case">· {t.qty.toFixed(6)}</span></div>
                  <div className="text-[11px] text-slate-500">{new Date(t.created_at).toLocaleString()} · @ {fmtUsd(t.price)}</div>
                </div>
                <div className="font-mono-ticker text-sm">{fmtUsd(t.usd_amount)}</div>
              </li>
            ))}
          </ul>
          <div className="overflow-x-auto hidden sm:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] font-mono-ticker uppercase tracking-widest text-slate-500 border-b border-slate-900">
                  <th className="text-left px-4 py-2">When</th>
                  <th className="text-left px-4 py-2">Asset</th>
                  <Th k="pt_trade_side" className="text-left">Side</Th>
                  <Th k="pt_qty" tid="pf-hist-th-pt_qty" className="text-right">Qty</Th>
                  <Th k="pt_trade_price" className="text-right hidden sm:table-cell">Price</Th>
                  <Th k="pt_trade_usd" className="text-right">USD</Th>
                </tr>
              </thead>
              <tbody data-testid="trades-list">
                {trades.map((t) => (
                  <tr key={t.id} className="border-b border-slate-900">
                    <td className="px-4 py-2 text-xs text-slate-400">{new Date(t.created_at).toLocaleString()}</td>
                    <td className="px-4 py-2 font-mono-ticker uppercase text-xs">{t.symbol}</td>
                    <td className={`px-4 py-2 text-xs font-semibold ${t.side === "buy" ? "text-emerald-400" : "text-red-400"}`}>
                      {t.side.toUpperCase()}
                    </td>
                    <td className="px-4 py-2 text-right font-mono-ticker">{t.qty.toFixed(6)}</td>
                    <td className="px-4 py-2 text-right font-mono-ticker hidden sm:table-cell">{fmtUsd(t.price)}</td>
                    <td className="px-4 py-2 text-right font-mono-ticker">{fmtUsd(t.usd_amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>)}
      </div>

      <TradeModal
        open={modal.open}
        onOpenChange={(v) => setModal((m) => ({ ...m, open: v }))}
        side={modal.side}
        coin={modal.coin}
        onDone={load}
      />
    </div>
  );
}
